import { useEffect, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import Blockquote from "@tiptap/extension-blockquote";
import Link from "@tiptap/extension-link";
import { IndentedParagraph } from "./indent-paragraph";
import { IndentedBulletList } from "./indent-bullet-list";
import { IndentedOrderedList } from "./indent-order-list";
import BasicMenuBar from "./basic-menu-bar";
import HyperLinkModal from "./HyperLinkModal";
import { cn } from "@/utils/theme";

interface BasicRichTextEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
  label?: string;
  helperText?: string;
  error?: string;
  required?: boolean;
  showHeadings?: boolean;
  showAlignment?: boolean;
  showIndent?: boolean;
  onInsertVariable?: () => void;
  integrated?: boolean; // Modo integrado sin bordes externos ni border-radius
  onBlur?: () => void;
  labelClassName?: string;
}

export default function BasicRichTextEditor({
  value = "",
  onChange,
  placeholder,
  minHeight = "156px",
  maxHeight,
  label,
  helperText,
  error,
  required = false,
  showHeadings = false,
  showAlignment = false,
  showIndent = false,
  onInsertVariable,
  integrated = false,
  onBlur,
  labelClassName,
}: BasicRichTextEditorProps) {
  const [modalState, setModalState] = useState<"idle" | "link">("idle");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          HTMLAttributes: {
            class: "list-disc ml-3",
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: "list-decimal ml-3",
          },
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
        alignments: ["left", "center", "right", "justify"],
      }),
      Blockquote,
      Highlight,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
      IndentedParagraph,
      IndentedBulletList,
      IndentedOrderedList,
      TextStyle,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: `border rounded-md bg-neutral-object py-2 px-3 overflow-y-auto`,
        style: `min-height: ${minHeight}; ${maxHeight ? `max-height: ${maxHeight};` : ""}`,
        placeholder: placeholder || "",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
  });

  useEffect(() => {
    if (editor && value !== undefined) {
      const currentContent = editor.getHTML();
      // Solo actualizar si el contenido es diferente para evitar loops
      if (value !== currentContent) {
        editor.commands.setContent(value || "", false);
      }
    }
  }, [value, editor]);

  const onConfirmLink = (url: string, text: string) => {
    if (!editor) return;
    if (editor.isActive("link")) {
      editor.chain().focus().extendMarkRange("link").run();
    }

    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to);

    // Determine if we need to replace text
    const shouldReplaceText = text && text !== selectedText;

    if (shouldReplaceText) {
      // Replace entire selection with new text and link
      editor
        .chain()
        .focus()
        .command(({ tr }) => {
          tr.insertText(text, from, to);
          return true;
        })
        .setTextSelection({ from, to: from + text.length })
        .setLink({ href: url })
        .run();
    } else {
      // Just update/set the link
      editor.chain().focus().setLink({ href: url }).run();
    }

    setModalState("idle");
  };

  if (!editor) return null;

  return (
    <div
      className={
        integrated
          ? "flex flex-col w-full h-full"
          : "flex flex-col w-full gap-1.5"
      }
    >
      {!integrated && label && (
        <label
          className={cn(
            "font-semibold text-body-loud text-sm leading-2xl",
            labelClassName,
          )}
        >
          {label}
          {required && <span className="text-danger ml-0.5">*</span>}
        </label>
      )}

      {!integrated && helperText && !error && (
        <span className="text-body-quiet text-xs leading-xl font-normal">
          {helperText}
        </span>
      )}

      <div
        className={
          integrated
            ? "flex flex-col w-full h-full"
            : "flex flex-col w-full border border-neutral rounded-md overflow-hidden"
        }
      >
        <div
          className={
            integrated
              ? "gap-2 flex flex-col 2xl:flex-row justify-center 2xl:justify-between p-3 bg-neutral-object-accent-quiet border-b border-neutral"
              : "gap-2 flex flex-col 2xl:flex-row justify-center 2xl:justify-between p-3 bg-neutral-object-accent-quiet border-b border-neutral"
          }
        >
          <BasicMenuBar
            editor={editor}
            showHeadings={showHeadings}
            showAlignment={showAlignment}
            showIndent={showIndent}
            onInsertVariable={onInsertVariable}
            onHyperlink={() => {
              setModalState("link");
            }}
          />
        </div>

        {modalState === "link" && (
          <HyperLinkModal
            onClose={() => {
              setModalState("idle");
            }}
            onConfirm={onConfirmLink}
            editor={editor}
            onRemove={() => {
              editor.chain().focus().unsetLink().run();
              setModalState("idle");
            }}
          />
        )}

        <div
          className={maxHeight ? "overflow-y-auto" : ""}
          style={maxHeight ? { maxHeight } : undefined}
        >
          <EditorContent
            editor={editor}
            className="rich-text-content text-body-loud"
            data-testid="basic-rich-text-editor"
            onBlur={onBlur}
          />
        </div>
      </div>

      {!integrated && error && (
        <div className="text-danger flex items-center gap-1.5">
          <span className="text-current text-xs leading-xl font-normal">
            {error}
          </span>
        </div>
      )}
    </div>
  );
}
